import { __assign } from "tslib";
import { APIAccessLevel } from "../models/APIAccessLevel";
import { TreeItemType } from "../models/TreeItemType";
import { ChildLoadState } from "../models/ChildLoadState";
import { WorkspaceType } from "../models/WorkspaceType";
import { UniqueContainerSet } from "../UniqueContainerId/UniqueContainerSet";
import { containerTreeSort } from "../Utils/ContainerUtil";
import { UniqueContainerId } from "../UniqueContainerId/UniqueContainerId";
import { UniqueContainerIdSet } from "../UniqueContainerId/UniqueContainerIdSet";
export var FilterMode;
(function (FilterMode) {
    FilterMode["INCLUSIVE"] = "inclusive";
    FilterMode["EXCLUSIVE"] = "exclusive";
})(FilterMode || (FilterMode = {}));
/**
 * Converts the response from /workspaces into a containerTreeData object
 */
export var convertWorkspacesAPIResponse = function (apiResponse, options) {
    var comparator = getOptions(options).comparator;
    try {
        var results = parseResponse(apiResponse);
        var containerTreeData = [];
        for (var _i = 0, _a = results.data; _i < _a.length; _i++) {
            var result = _a[_i];
            containerTreeData.push({
                name: result.name,
                id: result.id.toString(),
                containerType: TreeItemType.WORKSPACE,
                accessLevel: result.accessLevel,
                childData: { loadState: ChildLoadState.not },
                isTemplate: false,
                permalink: result.permalink
            });
        }
        return __assign(__assign({}, results), { data: containerTreeData.sort(comparator) });
    }
    catch (e) {
        throw new Error("Unexpected error while parsing JSON");
    }
};
/**
 * Converts the response from /folders/{id} into a containerTreeData object
 */
export var convertFolderAPIResponse = function (apiResponse, options) {
    var optionSet = getOptions(options);
    try {
        var result = parseResponse(apiResponse);
        return convertParentObject(result, TreeItemType.FOLDER, optionSet, false);
    }
    catch (e) {
        throw new Error("Unexpected error while parsing JSON");
    }
};
/**
 * Converts the response from /workspace/{id} into a containerTreeData object
 */
export var convertWorkspaceAPIResponse = function (apiResponse, options) {
    var optionSet = getOptions(options);
    try {
        var result = parseResponse(apiResponse);
        return convertParentObject(result, TreeItemType.WORKSPACE, optionSet, false);
    }
    catch (e) {
        throw new Error("Unexpected error while parsing JSON");
    }
};
/**
 * Converts the response from /navigation/search into a containerTreeData object
 */
export var convertSearchAPIResponse = function (apiResponse, options) {
    var optionSet = getOptions(options);
    try {
        var results = parseResponse(apiResponse);
        var containerTreeSearchResults = [];
        var map = new UniqueContainerSet();
        for (var _i = 0, results_1 = results; _i < results_1.length; _i++) {
            var result = results_1[_i];
            containerTreeSearchResults.push(convertRecursive(result, optionSet, map));
        }
        return {
            data: containerTreeSearchResults.sort(optionSet.comparator),
            map: map
        };
    }
    catch (e) {
        throw new Error("Unexpected error while parsing JSON");
    }
};
/**
 * Converts the response from /folders/personal into a containerTreeObject
 */
export var convertPersonalFolderAPIResponse = function (apiResponse, options) {
    var optionSet = getOptions(options);
    try {
        var result = parseResponse(apiResponse);
        return convertParentObject(result, TreeItemType.FOLDER, optionSet, false, true);
    }
    catch (e) {
        throw new Error("Unexpected error while parsing JSON");
    }
};
export var insertChildIntoTree = function (containerTree, nodeToInsert) {
    var newTree = [];
    for (var _i = 0, containerTree_1 = containerTree; _i < containerTree_1.length; _i++) {
        var node = containerTree_1[_i];
        newTree.push(insertChildren(node, nodeToInsert));
    }
    return newTree;
};
var parseResponse = function (response) {
    if (typeof response === "string") {
        return JSON.parse(response);
    }
    return response;
};
var insertChildren = function (currentNode, nodeToInsert) {
    var _a;
    // If it's the same node, replace this node.
    if (currentNode.id === nodeToInsert.id &&
        currentNode.containerType === nodeToInsert.containerType) {
        return nodeToInsert;
    }
    // If the children are loaded, replace the array with a new array called recursively
    if (((_a = currentNode.childData) === null || _a === void 0 ? void 0 : _a.loadState) === ChildLoadState.loaded) {
        var newChildren = [];
        for (var _i = 0, _b = currentNode.childData.childrenData; _i < _b.length; _i++) {
            var child = _b[_i];
            newChildren.push(insertChildren(child, nodeToInsert));
        }
        return __assign(__assign({}, currentNode), { childData: {
                loadState: ChildLoadState.loaded,
                childrenData: newChildren
            } });
    }
    // If the node has no children, return a pointer to the node.
    return __assign({}, currentNode);
};
var convertRecursive = function (parentObject, options, map) {
    var _a;
    var isPersonalWorkspace = parentObject.workspaceType &&
        parentObject.workspaceType === WorkspaceType.PERSONAL;
    var workspaceOrFolder = {
        id: parentObject.id.toString(),
        name: parentObject.name,
        isTemplate: false,
        permalink: parentObject.permalink,
        containerType: parentObject.workspaceType &&
            parentObject.workspaceType === WorkspaceType.SHARED
            ? TreeItemType.WORKSPACE
            : TreeItemType.FOLDER,
        childData: {
            loadState: ChildLoadState.loaded,
            childrenData: []
        }
    };
    if (parentObject.accessLevel) {
        workspaceOrFolder.accessLevel = parentObject.accessLevel;
    }
    if (isPersonalWorkspace) {
        workspaceOrFolder.isPersonalWorkspace = true;
    }
    addDisplayObjects(workspaceOrFolder, parentObject, options);
    var comparator = options.comparator;
    if (parentObject.folders) {
        if (((_a = workspaceOrFolder.childData) === null || _a === void 0 ? void 0 : _a.loadState) === ChildLoadState.loaded) {
            var allChildren = [];
            for (var _i = 0, _b = parentObject.folders; _i < _b.length; _i++) {
                var childApiFolder = _b[_i];
                allChildren.push(convertRecursive(childApiFolder, options, map));
            }
            workspaceOrFolder.childData.childrenData = workspaceOrFolder.childData.childrenData.concat(allChildren);
            workspaceOrFolder.childData.childrenData.sort(comparator);
        }
    }
    map.add(workspaceOrFolder);
    return workspaceOrFolder;
};
var getOptions = function (options) {
    var optionSet = {
        comparator: containerTreeSort,
        excludeIds: new UniqueContainerIdSet(),
        filter: {
            mode: FilterMode.EXCLUSIVE,
            filterSet: new Set()
        },
        includeTemplates: false
    };
    if (options) {
        if (options.comparator) {
            optionSet.comparator = options.comparator;
        }
        if (options.filter) {
            optionSet.filter = options.filter;
        }
        if (options.excludeIds) {
            optionSet.excludeIds = options.excludeIds;
        }
        if (options.includeTemplates) {
            optionSet.includeTemplates = options.includeTemplates;
        }
    }
    return optionSet;
};
var addChildren = function (parentObject, childObjects, containerType, isTemplate, excludeIds) {
    var _a;
    // new UniqueContainerId(value.id, value.containerType)
    var children = [];
    for (var _i = 0, childObjects_1 = childObjects; _i < childObjects_1.length; _i++) {
        var child = childObjects_1[_i];
        var uniqueContainerId = new UniqueContainerId(String(child.id), containerType);
        if (!excludeIds.has(uniqueContainerId)) {
            var containerTreeData = {
                id: child.id.toString(),
                name: child.name,
                permalink: child.permalink,
                containerType: containerType,
                isTemplate: isTemplate
            };
            if (child.accessLevel) {
                containerTreeData.accessLevel = child.accessLevel;
            }
            if (containerType === TreeItemType.FOLDER) {
                containerTreeData.childData = { loadState: ChildLoadState.not };
            }
            children.push(containerTreeData);
        }
    }
    if (((_a = parentObject.childData) === null || _a === void 0 ? void 0 : _a.loadState) === ChildLoadState.loaded) {
        parentObject.childData.childrenData = parentObject.childData.childrenData.concat(children);
    }
};
var addDisplayObjects = function (folder, apiObject, options) {
    var includeTemplates = options.includeTemplates, filter = options.filter, excludeIds = options.excludeIds;
    if (apiObject.reports && isFiltered(filter, TreeItemType.REPORT)) {
        addChildren(folder, apiObject.reports, TreeItemType.REPORT, false, excludeIds);
    }
    if (apiObject.sheets && isFiltered(filter, TreeItemType.SHEET)) {
        addChildren(folder, apiObject.sheets, TreeItemType.SHEET, false, excludeIds);
    }
    // The sights should be deprecated once we are onto Solutions Service completely
    if (apiObject.sights && isFiltered(filter, TreeItemType.DASHBOARD)) {
        addChildren(folder, apiObject.sights, TreeItemType.DASHBOARD, false, excludeIds);
    }
    // Adding extra check for dashboards in case any API returns both sights and dashboards (which should not happen)
    if (!apiObject.sights &&
        apiObject.dashboards &&
        isFiltered(filter, TreeItemType.DASHBOARD)) {
        addChildren(folder, apiObject.dashboards, TreeItemType.DASHBOARD, false, excludeIds);
    }
    if (apiObject.templates && includeTemplates) {
        addChildren(folder, apiObject.templates, TreeItemType.SHEET, true, excludeIds);
    }
};
var convertParentObject = function (result, parentType, options, isTemplate, isPersonalWorkspace) {
    var _a;
    var comparator = options.comparator, filter = options.filter, excludeIds = options.excludeIds;
    var folder = {
        id: result.id.toString(),
        name: result.name,
        containerType: parentType,
        accessLevel: result.accessLevel,
        permalink: result.permalink,
        isTemplate: isTemplate,
        childData: { loadState: ChildLoadState.loaded, childrenData: [] }
    };
    if (isPersonalWorkspace) {
        folder.isPersonalWorkspace = true;
        folder.accessLevel = APIAccessLevel.OWNER;
    }
    addDisplayObjects(folder, result, options);
    if (result.folders && isFiltered(filter, TreeItemType.FOLDER)) {
        addChildren(folder, result.folders, TreeItemType.FOLDER, false, excludeIds);
    }
    if (((_a = folder.childData) === null || _a === void 0 ? void 0 : _a.loadState) === ChildLoadState.loaded) {
        folder.childData.childrenData.sort(comparator);
    }
    return folder;
};
var isFiltered = function (filter, TreeItemType) {
    return ((filter.mode === FilterMode.EXCLUSIVE &&
        !filter.filterSet.has(TreeItemType)) ||
        (filter.mode === FilterMode.INCLUSIVE &&
            filter.filterSet.has(TreeItemType)));
};
//# sourceMappingURL=conversionMethods.js.map